import React, { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import ProfileSection from 'app/common/pixel-menu/_comp/ProfileSection'
import { getProfitAnalyticsHeaders } from 'client/utils/api-routes-analytics'
import {
  colors,
  DcBreadcrumbs,
  DcButton,
  DcDiv,
  DcFlexBox,
  PixelPageContainer,
  PixelText,
  Section,
  SectionContent,
} from 'pixel-base'
import styled from 'styled-components'

import { ProfitHeader } from './types'
import {
  formatCount,
  formatPKR,
  formatReportDate,
  formatReportDateMeta,
  getHeaderTotalCost,
  getHeaderTotalSale,
  getProfitMargin,
  resolveReportDate,
} from './utils'

const breadcrumbs = [
  { name: 'Analytics', active: false },
  { name: 'Profit Analytics Engine', active: true },
]

const DEFAULT_FILTERS = {
  page: 0,
  page_size: 31,
  date: '',
  start_date: '',
  end_date: '',
}

const ProfitAnalytics: React.FC = () => {
  const navigate = useNavigate()
  const [headers, setHeaders] = useState<ProfitHeader[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [totalRows, setTotalRows] = useState(0)
  const [filters, setFilters] = useState(DEFAULT_FILTERS)

  const loadHeaders = useCallback(() => {
    setIsLoading(true)
    setError(null)

    getProfitAnalyticsHeaders(filters).subscribe({
      next: (res: any) => {
        const response = res?.response || {}
        setHeaders(response.data || [])
        setTotalRows(response._meta?.total || 0)
        setIsLoading(false)
      },
      error: () => {
        setError('Failed to fetch profit headers')
        setHeaders([])
        setIsLoading(false)
      },
    })
  }, [filters])

  useEffect(() => {
    loadHeaders()
  }, [loadHeaders])

  const handleRowClick = (header: ProfitHeader) => {
    const dateStr = resolveReportDate(header.report_date)
    const params = new URLSearchParams({
      d: dateStr,
      c: String(header.product_count),
      q: String(header.total_qty_sold),
      ts: String(getHeaderTotalSale(header)),
      tc: String(getHeaderTotalCost(header)),
      ds: String(header.total_discount_amount),
      p: String(header.total_simple_profit),
    })
    navigate(`/analytics/profit-analytics/${header.id}?${params.toString()}`)
  }

  const clearFilters = () => {
    setFilters(DEFAULT_FILTERS)
  }

  const hasActiveFilters = Boolean(
    filters.date || filters.start_date || filters.end_date,
  )

  return (
    <DcFlexBox flexDirection="column">
      <DcFlexBox
        padding="0px 20px"
        height="60px"
        style={{
          justifyContent: 'space-between',
          borderBottom: `1px solid ${colors.grayBorderLight}`,
        }}
        alignItems="center"
      >
        <DcFlexBox alignItems="center" width="auto">
          <DcBreadcrumbs fontSize="16px" data={breadcrumbs} />
        </DcFlexBox>
        <DcDiv>
          <ProfileSection />
        </DcDiv>
      </DcFlexBox>

      <PixelPageContainer padding="0px">
        <Section style={{ padding: '20px' }}>
          <SectionContent style={{ padding: '20px', borderRadius: '8px' }}>
            <HeaderRow>
              <div>
                <PageTitle>Profit Analytics Engine</PageTitle>
                <PageSubtitle>
                  Detailed product-level profit insights and reports
                </PageSubtitle>
              </div>
              <DcButton
                variant="outline-secondary"
                onClick={loadHeaders}
                disabled={isLoading}
              >
                {isLoading ? 'Refreshing...' : 'Refresh'}
              </DcButton>
            </HeaderRow>

            <FiltersCard>
              <FilterGroup>
                <FilterLabel>Single Date</FilterLabel>
                <DateInput
                  type="date"
                  value={filters.date}
                  onChange={(e) =>
                    setFilters((prev) => ({
                      ...prev,
                      date: e.target.value,
                      start_date: '',
                      end_date: '',
                      page: 0,
                    }))
                  }
                />
              </FilterGroup>

              <FilterDivider />

              <FilterGroup $grow>
                <FilterLabel>Date Range</FilterLabel>
                <RangeRow>
                  <DateInput
                    type="date"
                    value={filters.start_date}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        start_date: e.target.value,
                        date: '',
                        page: 0,
                      }))
                    }
                  />
                  <RangeSeparator>→</RangeSeparator>
                  <DateInput
                    type="date"
                    value={filters.end_date}
                    onChange={(e) =>
                      setFilters((prev) => ({
                        ...prev,
                        end_date: e.target.value,
                        date: '',
                        page: 0,
                      }))
                    }
                  />
                </RangeRow>
              </FilterGroup>

              {hasActiveFilters && (
                <DcButton variant="outline-secondary" onClick={clearFilters}>
                  Clear Filters
                </DcButton>
              )}
            </FiltersCard>

            {error && (
              <ErrorCard>
                <PixelText style={{ color: '#dc2626', marginBottom: 12 }}>
                  {error}
                </PixelText>
                <DcButton variant="outline-secondary" onClick={loadHeaders}>
                  Retry Load
                </DcButton>
              </ErrorCard>
            )}

            <TableCard>
              <TableHeader>
                <PixelText fontSize="16px" fontWeight="600">
                  Daily Profit Summaries
                </PixelText>
              </TableHeader>
              <TableScroll>
                <Table>
                  <thead>
                    <tr>
                      <th>Report Date</th>
                      <th className="center">Products</th>
                      <th className="center">Qty Sold</th>
                      <th>Total Sale</th>
                      <th>Total Cost</th>
                      <th>Discount</th>
                      <th>Net Profit</th>
                      <th />
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading ? (
                      Array.from({ length: 5 }).map((_, i) => (
                        <tr key={i}>
                          {Array.from({ length: 8 }).map((__, j) => (
                            <td key={j}>
                              <Skeleton />
                            </td>
                          ))}
                        </tr>
                      ))
                    ) : headers.length === 0 ? (
                      <tr>
                        <td colSpan={8}>
                          <EmptyState>
                            No profit reports found for the selected criteria
                          </EmptyState>
                        </td>
                      </tr>
                    ) : (
                      headers.map((header) => (
                        <tr
                          key={header.id}
                          onClick={() => handleRowClick(header)}
                        >
                          <td>
                            <DateCell>
                              <strong>
                                {formatReportDate(header.report_date)}
                              </strong>
                              <span>
                                {formatReportDateMeta(header.report_date)}
                              </span>
                            </DateCell>
                          </td>
                          <td className="center">
                            <Pill $tone="blue">
                              {formatCount(header.product_count)}
                            </Pill>
                          </td>
                          <td className="center">
                            <Pill $tone="purple">
                              {formatCount(header.total_qty_sold)}
                            </Pill>
                          </td>
                          <td>
                            <Amount $tone="sale">
                              {formatPKR(getHeaderTotalSale(header))}
                            </Amount>
                          </td>
                          <td>
                            <Amount $tone="muted">
                              {formatPKR(getHeaderTotalCost(header))}
                            </Amount>
                          </td>
                          <td>
                            <Pill $tone="red">
                              -
                              {formatPKR(
                                Math.abs(header.total_discount_amount ?? 0),
                              )}
                            </Pill>
                          </td>
                          <td>
                            <ProfitCell>
                              <Amount
                                $tone={
                                  header.total_simple_profit >= 0
                                    ? 'profit'
                                    : 'loss'
                                }
                              >
                                {formatPKR(header.total_simple_profit)}
                              </Amount>
                              <Margin $positive={getProfitMargin(header) >= 0}>
                                {getProfitMargin(header).toFixed(1)}% margin
                              </Margin>
                            </ProfitCell>
                          </td>
                          <td>
                            <Chevron>›</Chevron>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </Table>
              </TableScroll>

              {!isLoading && totalRows > filters.page_size && (
                <Pagination>
                  <DcButton
                    variant="outline-secondary"
                    disabled={filters.page === 0}
                    onClick={() =>
                      setFilters((prev) => ({
                        ...prev,
                        page: prev.page - 1,
                      }))
                    }
                  >
                    Previous
                  </DcButton>
                  <DcButton
                    variant="outline-secondary"
                    disabled={
                      (filters.page + 1) * filters.page_size >= totalRows
                    }
                    onClick={() =>
                      setFilters((prev) => ({
                        ...prev,
                        page: prev.page + 1,
                      }))
                    }
                  >
                    Next
                  </DcButton>
                </Pagination>
              )}
            </TableCard>
          </SectionContent>
        </Section>
      </PixelPageContainer>
    </DcFlexBox>
  )
}

const HeaderRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
`

const PageTitle = styled.h1`
  margin: 0;
  font-size: 24px;
  font-weight: 700;
  color: ${colors.black || '#111827'};
`

const PageSubtitle = styled.p`
  margin: 4px 0 0;
  color: #6b7280;
  font-size: 14px;
`

const FiltersCard = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
  gap: 20px;
  padding: 16px;
  margin-bottom: 20px;
  background: #f8fafc;
  border-radius: 8px;
`

const FilterGroup = styled.div<{ $grow?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: ${(props) => (props.$grow ? '1 1 320px' : '0 1 240px')};
  max-width: ${(props) => (props.$grow ? '450px' : '280px')};
`

const FilterLabel = styled.label`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: #6b7280;
`

const DateInput = styled.input`
  height: 40px;
  padding: 0 12px;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #fff;
  font-size: 14px;
  color: #111827;
  width: 100%;

  &:focus {
    outline: none;
    border-color: #7c3aed;
  }
`

const RangeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`

const RangeSeparator = styled.span`
  color: #9ca3af;
  flex-shrink: 0;
`

const FilterDivider = styled.div`
  width: 1px;
  height: 48px;
  background: #e5e7eb;
  align-self: center;

  @media (max-width: 900px) {
    display: none;
  }
`

const ErrorCard = styled.div`
  padding: 20px;
  margin-bottom: 20px;
  text-align: center;
  background: #fef2f2;
  border: 1px solid #fecaca;
  border-radius: 8px;
`

const TableCard = styled.div`
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
`

const TableHeader = styled.div`
  padding: 14px 20px;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
`

const TableScroll = styled.div`
  overflow-x: auto;
`

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  min-width: 900px;

  th {
    padding: 12px 16px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.06em;
    text-transform: uppercase;
    color: #6b7280;
    text-align: left;
    background: #fafafa;
    border-bottom: 1px solid #e5e7eb;

    &.center {
      text-align: center;
    }

    &:first-child {
      padding-left: 20px;
    }
  }

  td {
    padding: 12px 16px;
    border-bottom: 1px solid #f3f4f6;
    vertical-align: middle;

    &.center {
      text-align: center;
    }

    &:first-child {
      padding-left: 20px;
    }
  }

  tbody tr {
    cursor: pointer;
    transition: background 0.15s ease;

    &:hover {
      background: #f8fafc;
    }
  }
`

const Skeleton = styled.div`
  height: 14px;
  width: 70%;
  border-radius: 4px;
  background: #e5e7eb;
  animation: pulse 1.2s ease-in-out infinite;

  @keyframes pulse {
    0%,
    100% {
      opacity: 1;
    }
    50% {
      opacity: 0.45;
    }
  }
`

const EmptyState = styled.div`
  padding: 48px 16px;
  text-align: center;
  color: #6b7280;
`

const DateCell = styled.div`
  display: flex;
  flex-direction: column;
  line-height: 1.25;

  strong {
    font-size: 14px;
    color: #111827;
  }

  span {
    margin-top: 2px;
    font-size: 11px;
    letter-spacing: 0.04em;
    color: #9ca3af;
  }
`

const Pill = styled.span<{ $tone: 'blue' | 'purple' | 'red' }>`
  display: inline-flex;
  min-width: 74px;
  justify-content: center;
  padding: 4px 12px;
  border-radius: 999px;
  font-size: 12px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  background: ${(props) =>
    props.$tone === 'blue'
      ? '#eff6ff'
      : props.$tone === 'purple'
      ? '#f5f3ff'
      : '#fef2f2'};
  color: ${(props) =>
    props.$tone === 'blue'
      ? '#1d4ed8'
      : props.$tone === 'purple'
      ? '#6d28d9'
      : '#dc2626'};
`

const Amount = styled.span<{
  $tone: 'sale' | 'muted' | 'profit' | 'loss'
}>`
  font-weight: ${(props) => (props.$tone === 'muted' ? 500 : 700)};
  font-variant-numeric: tabular-nums;
  color: ${(props) =>
    props.$tone === 'sale'
      ? '#047857'
      : props.$tone === 'profit'
      ? '#16a34a'
      : props.$tone === 'loss'
      ? '#dc2626'
      : '#6b7280'};
`

const ProfitCell = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  line-height: 1.25;
`

const Margin = styled.span<{ $positive: boolean }>`
  margin-top: 2px;
  font-size: 11px;
  font-weight: 500;
  color: ${(props) =>
    props.$positive ? 'rgba(22, 163, 74, 0.8)' : 'rgba(220, 38, 38, 0.8)'};
`

const Chevron = styled.span`
  color: #9ca3af;
  font-size: 20px;
  font-weight: 300;
`

const Pagination = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
  border-top: 1px solid #e5e7eb;
`

export default ProfitAnalytics
